import React from 'react';
import PropTypes from 'prop-types';

import { Box, Card, Chip, Stack, Typography } from '@mui/material';

import { log } from 'src/utils/logger';
import { countryLabel } from 'src/utils/platform/channel-label';
import { getPlatformInfo } from 'src/utils/platform/platform-info';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';

import { RADIUS } from 'src/theme/ui-tokens';

import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';

import PostCardList from './PostCardList';


/**
 * PostCardListRepost에서 사용할 데이터를 상황별로 정의하는 함수
 * 
 * @param {Object} post - 게시물 데이터
 * @returns {Object} { headerData, cardData, platformIconPath }
 *   - headerData: 헤더에 표시할 작성자 정보 (author, account, source)
 *   - cardData: PostCard에 전달할 게시물 데이터
 *   - platformIconPath: 플랫폼 아이콘 경로
 */
const getPostCardRepostData = (post: any) => {
  // 전처리 단계에서 설정된 플래그 사용
  const isQuote = Boolean(post?.isQuote);
  const isRepost = Boolean(post?.isRepost);
  const isComment = Boolean(post?.isComment);

  // originPost 데이터 (인용인 경우 필요)
  const originPostData = post?.originPost || post?._source?.originPost;

  // 플랫폼 정보
  const source = post?.source;
  const platformIconPath = source ? `/assets/icons/platforms/${String(source).toLowerCase()}.svg` : null;

  // 리포스트: 통일 규약 (post-ingest normalizeUnifiedRepostPost)
  if (isRepost) {
    const headerData = {
      repostAuthor: post?.repostAuthor || '',
      repostAccount: post?.repostAccount || '',
      author: post?.author || '',
      account: post?.account || '',
      source: post?.source,
    };

    const hasUsableOriginPost =
      originPostData &&
      typeof originPostData === 'object' &&
      Object.keys(originPostData).length > 0;

    const cardData = hasUsableOriginPost
      ? {
        ...originPostData,
        content: originPostData.text || originPostData.content,
        title: originPostData.title || originPostData.text,
        id: originPostData.postId || originPostData.id,
      }
      : {
        ...post,
        isRepost: true,
      };

    return {
      headerData,
      cardData,
      platformIconPath,
      isRepost: true,
      isQuote: false,
      isComment: false,
    };
  }

  // 상황 2: 댓글 (isComment = true)
  if (isComment && originPostData) {
    const headerData = {
      // 헤더: post 데이터 자체를 다 전달
      ...post,
    };
    const cardData = {
      // PostCard: originPost 객체를 전달
      ...originPostData,
      // 필드명 매핑
      content: originPostData.text || originPostData.content,
      title: originPostData.title || originPostData.text,
      id: originPostData.postId || originPostData.id,
      isComment: true,
    };

    return {
      headerData,
      cardData,
      platformIconPath,
      isComment,
      isQuote: false,
      isRepost: false,
    };
  }

  // 상황 3: 인용 (isQuote = true)
  if (isQuote && originPostData) {
    const headerData = {
      // 헤더: post 데이터 자체를 다 전달
      ...post,
    };
    const cardData = {
      // PostCard: originPost 객체를 전달
      ...originPostData,
      // 필드명 매핑
      content: originPostData.text || originPostData.content,
      title: originPostData.title || originPostData.text,
      id: originPostData.postId || originPostData.id,
      isQuote: true,
    };

    return {
      headerData,
      cardData,
      platformIconPath,
      isQuote,
      isRepost: false,
      isComment: false,
    };
  }

  // 그 외의 시나리오는 없음 (이 경우는 PostCardListRepost가 아닌 PostCardList로 표시되어야 함)
  // 하지만 안전을 위해 기본값 반환
  const headerData = {
    author: post?.author || '',
    account: post?.account || '',
    source: post?.source,
  };
  const cardData = {
    ...post,
  };

  // 리포스트도 인용도 아닌 글이 이 부품에 들어온 것 — 부르는 쪽의 분기 실수다. 글을 통째로 남기지 않는다
  log.debug('PostCardListRepost', '리포스트도 인용도 아닌 글이 들어왔다', {
    id: post?.id,
    source: post?.source,
    isQuote: post?.isQuote,
    isRepost: post?.isRepost,
  });

  return {
    headerData,
    cardData,
    platformIconPath,
    isQuote: false,
    isRepost: false,
    isComment: false,
  };
};

/**
 * 리포스트/인용/댓글 게시물 카드 컴포넌트 (리스트 뷰용)
 * 
 * 이 컴포넌트는 다음 상황에서 사용됩니다:
 * 1. 리포스트 (isPosted=true): 리포스트한 사용자 정보를 헤더에 표시하고, 원본 게시물을 PostCard에 표시
 * 2. 인용/댓글 (originPost 존재): 인용/댓글한 사용자 정보를 헤더에 표시하고, 원본 게시물(originPost)을 PostCard에 표시
 * 
 * @param {Object} post - 게시물 데이터
 * @param {Function} onClick - 카드 클릭 핸들러
 * @param {string} searchKeyword - 검색 키워드
 * @param {string} clientSearch - 클라이언트 검색어
 * @param {Array} topicKeywords - 토픽 키워드 배열
 * @param {Object} expandedSelectedBrands - 확장된 선택된 브랜드
 * @param {string} imagePosition - 이미지 위치 ('left' | 'right')
 * @param {number} index - 아이템 인덱스
 */
function PostCardListRepost({
  post,
  onClick,
  searchKeyword = '',
  clientSearch = '',
  topicKeywords = [],
  expandedSelectedBrands,
  imagePosition = 'right',
  index = 0,
  enableImageBookmark = false,
  enableCampaignAdd = false,
  isBookmark = false,
  selectedPostIds = new Set(),
  onToggleSelect,
}: {
  post: any
  onClick?: (post: any) => void
  searchKeyword?: string | readonly string[]
  clientSearch?: string
  topicKeywords?: readonly string[]
  /** ⚠️ `Set` 도 배열도 온다 — 코드가 `.has` 유무로 오리 타이핑한다 */
  expandedSelectedBrands?: any
  imagePosition?: 'left' | 'right'
  index?: number
  enableImageBookmark?: boolean
  enableCampaignAdd?: boolean
  isBookmark?: boolean
  selectedPostIds?: ReadonlySet<string>
  onToggleSelect?: (...args: any[]) => void
}) {
  // 상황별 데이터 가져오기
  const { headerData, cardData, platformIconPath, isQuote, isRepost, isComment } = getPostCardRepostData(post);

  // isQuoted일 때 헤더 데이터 준비
  const headerPost = isQuote ? headerData : null;
  const headerTimeDisplay = React.useMemo(() => {
    if (!headerPost) return '';
    if (headerPost?.timestamp) return formatTimestamp(headerPost.timestamp);
    if (headerPost?.date) return formatTimestamp(headerPost.date);
    return '';
  }, [headerPost]);

  // 헤더 메트릭 정보 계산
  const headerLatestMetrics = React.useMemo(() => {
    if (!headerPost) {
      return { likeCount: 0, commentCount: 0, viewCount: 0 };
    }
    return getMetricsByScoreTime(headerPost);
  }, [headerPost]);

  // 리포스트(겉 글) 메타: 수치/날짜/북마크는 "리포스트한 사람의 글(post)" 기준으로 표시
  const outerTimeDisplay = React.useMemo(() => {
    if (post?.timestamp) return formatTimestamp(post.timestamp)
    if (post?.date) return formatTimestamp(post.date)
    return ''
  }, [post?.timestamp, post?.date])

  const outerLatestMetrics = React.useMemo(() => getMetricsByScoreTime(post), [post])

  // market 정보 가져오기 (headerData에서)
  const market = headerData?.market || post?.market;
  const isHit = Boolean(headerData?.isHit || post?.isHit);

  // 리포스트면 나라를 안 적는다(원글의 나라라서) · 그 밖에는 공용 이름표(대문자 코드)
  // PostCard.jsx와 동일: external이거나 isRepost일 때 마켓 표시 숨김
  const marketDisplay = React.useMemo(() => {
    if (isRepost) return null;
    return market ? countryLabel(market) : null;
  }, [market, isRepost]);

  // sentiment 데이터 직접 추출
  const sentimentEvidencePositive = post?.sentimentEvidencePositive || [];
  const sentimentEvidenceNegative = readNegativeEvidence(post);

  // 플랫폼 정보
  const platformInfo = getPlatformInfo(headerData?.source || post?.source);

  // 텍스트 하이라이트 함수
  // ⚠️ 선존: 일부 호출부가 `highlightText(t, searchKeyword, topicKeywords)` 로 인자 셋을
  //    넘기지만 이 함수는 첫 인자만 읽는다(나머지는 클로저 값). 동작 보존을 위해 여분 인자를
  //    받기만 하고 쓰지 않는다 — 정리는 A475(후보 A560).
  const highlightText = (text: unknown, ..._ignoredExtraArgs: unknown[]) =>
    highlightTextWithHtmlRemoval(
      text as string,
      searchKeyword as string,
      topicKeywords as string[],
      clientSearch,
      sentimentEvidencePositive,
      sentimentEvidenceNegative
    );

  /**
   * 🔑 신원(계정명·작성자명)은 **마크업 없이** 그린다 — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   * 그 값에 태그가 필요한 이유가 없는데 HTML 통로를 열어 두면 «글쓴이가 고른 문자열»이
   * 태그가 된다. React 가 텍스트를 늘 escape 하므로 여기서는 태그가 생길 수 없다.
   */
  const renderIdentity = (text: string | null | undefined, prefix = '') => (
    <HighlightedText
      text={text}
      prefix={prefix}
      searchKeyword={searchKeyword as string}
      topicKeywords={topicKeywords as string[]}
      clientSearch={clientSearch}
      sentimentPositive={sentimentEvidencePositive}
      sentimentNegative={sentimentEvidenceNegative}
    />
  );

  return (
    <Card
      onClick={() => onClick && onClick(post)}
      sx={{
        borderRadius: 0,
        bgcolor: 'white',
        border: 'none',
        // PostCardList와 동일: 하단 구분선은 상위/피드에서 처리 (여기서는 미적용)
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative',
        pb: 2,
        pt: 2,
        width: '100%',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'primary.lighter',
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      {/* 댓글/인용/리포스트 표시: 칩으로 통일 (플래그 제거) */}
      {(isRepost || isQuote || isComment || Boolean(post?.isStory)) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: { xs: 0.75, sm: 1 }, flexWrap: 'wrap' }}>
          {isComment && (
            <Chip
              label="댓글"
              size="small"
              sx={{ height: 20, typography: 'uiAction', bgcolor: 'success.lighter', color: 'success.dark' }}
            />
          )}
          {isQuote && (
            <Chip
              label="인용"
              size="small"
              sx={{ height: 20, typography: 'uiAction', bgcolor: 'warning.lighter', color: 'warning.dark' }}
            />
          )}
          {isRepost && (
            <Chip
              label="리포스트"
              size="small"
              sx={{ height: 20, typography: 'uiAction', bgcolor: 'info.lighter', color: 'info.dark' }}
            />
          )}
          {Boolean(post?.isStory) && (
            <Chip
              label="스토리"
              size="small"
              sx={{ height: 20, typography: 'uiAction', bgcolor: 'error.lighter', color: 'error.dark' }}
            />
          )}
        </Stack>
      )}

      <Stack direction="column" spacing={1.5} sx={{ width: '100%' }}>
        {/* 헤더 정보 (한 줄) */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={{ xs: 0.75, sm: 1 }}
          sx={{ width: '100%', maxWidth: '100%', flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0, position: 'relative' }}
        >
          {/* 좌측: 플랫폼 아이콘 + 작성자/계정/마켓 + Hit 배지 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, overflow: 'hidden' }}>
            {platformIconPath && (
              <Box
                component="img"
                src={platformIconPath}
                alt={platformInfo?.label || headerData?.source}
                sx={{ width: 18, height: 18, flexShrink: 0 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, overflow: 'hidden' }}>
              {(headerData.repostAuthor || headerData.author) && (
                <Typography
                  sx={{ color: 'text.primary', fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                >
                  {renderIdentity(`${headerData.repostAuthor || headerData.author || ''}`)}
                </Typography>
              )}
              {(headerData.repostAccount || headerData.account) && (
                <Typography
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '12px', sm: '13.5px' },
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: '75%', sm: '60%' },
                  }}
                  title={`@${headerData.repostAccount || headerData.account}`}
                >
                  {renderIdentity(`${headerData.repostAccount || headerData.account || ''}`, '@')}
                </Typography>
              )}
            </Box>
            {marketDisplay && (
              <Typography color="text.secondary" variant="caption" sx={{ fontSize: { xs: '11px', sm: '12px' }, whiteSpace: 'nowrap' }}>
                {marketDisplay}
              </Typography>
            )}
            {isHit && (
              <Box sx={{
                px: 1,
                py: 0.25,
                borderRadius: RADIUS.sm,
                bgcolor: 'error.lighter',
                color: 'error.dark',
                fontSize: '11px',
                fontWeight: 600,
                flexShrink: 0,
              }}>
                HIT
              </Box>
            )}
          </Box>

        </Stack>

        {/* 쿼트일 때 제목/내용 - 원본 카드 위에 표시 */}
        {isQuote && headerPost && (() => {
          const quoteTitle = headerPost?.title || '';
          const quoteContent = headerPost?.content || '';
          const hasQuoteTitle = !!quoteTitle;
          const hasQuoteContent = !!quoteContent;

          if (!hasQuoteTitle && !hasQuoteContent) return null;

          return (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={{ xs: 1, sm: 1.5 }}
              sx={{ width: '100%', maxWidth: '100%', mt: 1.5 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {hasQuoteTitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: hasQuoteContent ? 1 : 0,
                      color: 'black',
                      display: '-webkit-box',
                      WebkitLineClamp: hasQuoteContent ? 1 : 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      fontSize: { xs: '13.5px', sm: '15px' },
                      lineHeight: { xs: 1.35, sm: 1.4 },
                      textAlign: 'left',
                    }}
                  >
                    <SafeHtml html={highlightText(quoteTitle, searchKeyword, topicKeywords)} />
                  </Typography>
                )}
                {hasQuoteContent && (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0,
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      lineHeight: { xs: 1.35, sm: 1.4 },
                      fontSize: { xs: '12px', sm: '13.5px' },
                      textAlign: 'left',
                    }}
                  >
                    <SafeHtml html={highlightText(quoteContent, searchKeyword, topicKeywords)} />
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })()}

        {/* 원본 PostCard 컴포넌트 - 테두리로 감싸기 */}
        <Box sx={{ position: 'relative', width: '100%', mt: 1 }}>


          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: RADIUS.sm,
              overflow: 'hidden',
              width: '100%',
              '& > .MuiCard-root': {
                bgcolor: 'grey.200',
              },
            }}
          >
            <PostCardList
              post={cardData}
              onClick={onClick}
              searchKeyword={searchKeyword}
              clientSearch={clientSearch}
              topicKeywords={topicKeywords}
              expandedSelectedBrands={expandedSelectedBrands}
              imagePosition={imagePosition}
              index={index}
              external
              hidePlatformIcon={false}
              showKindChips={false}
              showBookmarkButton={false}
              enableImageBookmark={enableImageBookmark}
              bookmarkTargetPost={post}
              isBookmark={isBookmark}
              selectedPostIds={selectedPostIds}
              onToggleSelect={onToggleSelect}
            />
          </Box>
        </Box>




        {/* 메트릭/날짜/북마크 - 겉 글 기준 (리포스트/인용 모두 표시) */}
        {(isQuote || isRepost) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: 'wrap' }}
          >
            {/* 좋아요 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: { xs: 20, sm: 22 },
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: { xs: 0.75, sm: 1 },
                py: 0,
              }}
            >
              <Box
                component="img"
                src="/assets/icons/icon_svg/like.svg"
                sx={{
                  width: { xs: 11, sm: 12 },
                  height: { xs: 11, sm: 12 },
                  filter: 'brightness(0) saturate(100%)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontWeight: 'Medium',
                  fontSize: { xs: '11px', sm: '12px' },
                }}
              >
                {formatNumber((isQuote ? headerLatestMetrics : outerLatestMetrics).likeCount)}
              </Typography>
            </Box>

            {/* 댓글 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: { xs: 20, sm: 22 },
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: { xs: 0.75, sm: 1 },
                py: 0,
              }}
            >
              <Box
                component="img"
                src="/assets/icons/icon_svg/reply.svg"
                sx={{
                  width: { xs: 11, sm: 12 },
                  height: { xs: 11, sm: 12 },
                  filter: 'brightness(0) saturate(100%)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontWeight: 'Medium',
                  fontSize: { xs: '11px', sm: '12px' },
                }}
              >
                {formatNumber((isQuote ? headerLatestMetrics : outerLatestMetrics).commentCount)}
              </Typography>
            </Box>

            {/* 조회수 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: { xs: 20, sm: 22 },
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: { xs: 0.75, sm: 1 },
                py: 0,
              }}
            >
              <Box
                component="img"
                src="/assets/icons/icon_svg/view.svg"
                sx={{
                  width: { xs: 11, sm: 12 },
                  height: { xs: 11, sm: 12 },
                  filter: 'brightness(0) saturate(100%)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontWeight: 'Medium',
                  fontSize: { xs: '11px', sm: '12px' },
                }}
              >
                {post?.contentsType === 'instagram_post' && (isQuote ? headerLatestMetrics : outerLatestMetrics).viewCount === 0
                  ? '-'
                  : formatNumber((isQuote ? headerLatestMetrics : outerLatestMetrics).viewCount)}
              </Typography>
            </Box>

            {/* 시간 정보 */}
            {(isQuote ? headerTimeDisplay : outerTimeDisplay) && (
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  ml: 'auto',
                  textAlign: 'right',
                  fontWeight: 'Light',
                  fontSize: '11px',
                  color: 'text.secondary',
                }}
              >
                {isQuote ? headerTimeDisplay : outerTimeDisplay}
              </Typography>
            )}

            {!isBookmark && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
                sx={{ pointerEvents: 'auto', flexShrink: 0 }}
              >
                <PostCardBookmarkOverlay
                  placement="postList"
                  post={post}
                  disabled={isRepost}
                  disabledTooltip={
                    isRepost ? '리포스트 포스팅은 북마크가 불가합니다.' : ''
                  }
                />
                {enableCampaignAdd && (
                  <PostCardCampaignOverlay
                    placement="postList"
                    post={post}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

PostCardListRepost.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    account: PropTypes.string,
    repostAuthor: PropTypes.string,
    repostAccount: PropTypes.string,
    source: PropTypes.string,
    imageUrl: PropTypes.string,
    imageS3Url: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    market: PropTypes.string,
    brands: PropTypes.arrayOf(PropTypes.string),
    canonicalBrands: PropTypes.arrayOf(PropTypes.string),
    isPosted: PropTypes.bool,
    sentimentEvidencePositive: PropTypes.arrayOf(PropTypes.string),
    sentimentEvidenceNegative: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onClick: PropTypes.func,
  searchKeyword: PropTypes.string,
  clientSearch: PropTypes.string,
  topicKeywords: PropTypes.arrayOf(PropTypes.string),
  expandedSelectedBrands: PropTypes.objectOf(PropTypes.bool),
  imagePosition: PropTypes.oneOf(['left', 'right']),
  index: PropTypes.number,
  enableImageBookmark: PropTypes.bool,
  enableCampaignAdd: PropTypes.bool,
};

export default React.memo(PostCardListRepost);
